import type { HttpPost } from '@/shared/infra/http'
import { getAnonymousId, getSessionId } from './telemetry-ids'
import { enqueueTelemetryEvent } from './telemetry-queue'
import type { ScreenKey } from './screen-registry'

export type FunnelEventType =
  | 'app_opened'
  | 'screen_viewed'
  | 'registration_started'
  | 'registration_step_viewed'
  | 'registration_step_completed'
  | 'registration_abandoned'
  | 'paywall_viewed'
  | 'pro_purchase_completed'

export type AppOpenedTrigger = 'cold_start' | 'foreground'

export type PaywallPlan = 'pro' | 'wu'

export type PurchaseRail = 'iap' | 'stripe'

export const CC_PLATFORM = 'cc_web'

type EmitFunnelEventParams = {
  eventType: FunnelEventType
  step?: string
  source?: string
  trigger?: AppOpenedTrigger
  screen?: ScreenKey
  previousScreen?: ScreenKey
  plan?: PaywallPlan
  rail?: PurchaseRail
  httpClient: HttpPost
}

type FunnelEventPayload = {
  eventType: FunnelEventType
  anonymousId: string
  sessionId: string
  platform: typeof CC_PLATFORM
  source?: string
  occurredAt: string
  idempotencyKey: string
  metadata?: Record<string, string>
}

function buildMetadata(params: EmitFunnelEventParams): Record<string, string> | undefined {
  switch (params.eventType) {
    case 'app_opened':
      return params.trigger ? { trigger: params.trigger } : undefined
    case 'screen_viewed': {
      if (!params.screen) return undefined
      const metadata: Record<string, string> = { screen: params.screen }
      if (params.previousScreen) metadata.previousScreen = params.previousScreen
      return metadata
    }
    case 'registration_started':
      return undefined
    case 'registration_step_viewed':
    case 'registration_step_completed':
      return { step: params.step ?? '' }
    case 'registration_abandoned':
      return params.step ? { step: params.step } : undefined
    case 'paywall_viewed':
      return params.plan ? { plan: params.plan } : undefined
    case 'pro_purchase_completed':
      return params.plan && params.rail ? { plan: params.plan, rail: params.rail } : undefined
  }
}

export async function emitFunnelEvent(params: EmitFunnelEventParams): Promise<void> {
  try {
    const { eventType, source, httpClient } = params
    const sessionId = getSessionId()
    const metadata = buildMetadata(params)

    const payload: FunnelEventPayload = {
      eventType,
      anonymousId: getAnonymousId(),
      sessionId,
      platform: CC_PLATFORM,
      occurredAt: new Date().toISOString(),
      idempotencyKey: `${eventType}:${sessionId}:${Date.now()}`,
    }
    if (source) {
      payload.source = source
    }
    if (metadata) {
      payload.metadata = metadata
    }

    enqueueTelemetryEvent(payload, httpClient)
  } catch {
    return
  }
}
